import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IconBell, IconX } from '@tabler/icons-react'
import Button from './ui/Button'
import { useToast } from './Toast'
import { pushSupported, pushPermission, enablePush } from '../api/push'

// One-time "turn on real-time theft alerts" prompt, shown after first login.
// The decision (allow OR dismiss) is remembered in localStorage so it never
// nags again — matching the brief. Only renders when the browser supports push
// and the user hasn't already granted/blocked it.
const STORAGE_KEY = 'tg_push_prompted'

export default function PushPrompt() {
  const { t } = useTranslation()
  const toast = useToast()
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!pushSupported()) return
    if (pushPermission() !== 'default') return       // already granted or blocked
    if (localStorage.getItem(STORAGE_KEY)) return     // already asked once
    setVisible(true)
  }, [])

  function remember() {
    localStorage.setItem(STORAGE_KEY, '1')
    setVisible(false)
  }

  async function allow() {
    setBusy(true)
    try {
      const ok = await enablePush()
      remember()
      if (ok) toast.success(t('push.enabled'))
      else if (pushPermission() === 'denied') toast.warning(t('push.blocked'))
      else toast.error(t('push.failed'))
    } catch {
      remember()
      toast.error(t('push.failed'))
    } finally {
      setBusy(false)
    }
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label={t('push.title')}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 'var(--tg-space-3)',
        background: 'var(--tg-bg-accent-subtle, #ECFDFA)',
        border: '1px solid var(--tg-teal-200, #97EDDC)',
        borderLeft: '4px solid var(--tg-teal-500, #14B8A6)',
        borderRadius: 'var(--tg-radius-lg)',
        padding: '14px 16px',
        marginBottom: 'var(--tg-space-4)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
          background: 'var(--tg-teal-500, #14B8A6)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <IconBell size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--tg-text-sm, 14px)', fontWeight: 600, color: 'var(--tg-text-primary, #111)', marginBottom: 2 }}>
          {t('push.title')}
        </div>
        <div style={{ fontSize: 'var(--tg-text-xs, 12px)', color: 'var(--tg-text-secondary, #555)', lineHeight: 1.5, marginBottom: 10 }}>
          {t('push.body')}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="primary" size="sm" loading={busy} onClick={allow}>
            {t('push.allow')}
          </Button>
          <Button variant="secondary" size="sm" disabled={busy} onClick={remember}>
            {t('push.later')}
          </Button>
        </div>
      </div>
      <button
        onClick={remember}
        aria-label={t('common.close')}
        style={{ background: 'none', border: 'none', color: 'var(--tg-text-secondary, #888)', cursor: 'pointer', padding: 2, lineHeight: 1 }}
      >
        <IconX size={16} />
      </button>
    </div>
  )
}
